import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { BookDemoDialog } from "@/components/book-demo-dialog";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { seo } from "@/lib/seo";
import { product, faqPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

export const Route = createFileRoute("/pricing")({
  head: () =>
    seo({
      title: "Pricing: AI Marketing Autopilot Plans & Cost",
      description:
        "Axy.digital pricing — autonomous Agentic Marketing or Done-For-You with a dedicated strategist. No long-term contracts.",
      path: "/pricing",
      jsonLd: [
        product({
          name: "Axy.digital — Agentic Marketing",
          description:
            "The autonomous algorithmic marketing engine. Detects market demand and turns it into GEO, LinkedIn, and X campaigns automatically.",
          url: `${SITE_URL}/pricing`,
          offers: [
            { name: "1 week free trial", price: 0, priceCurrency: "USD" },
            { name: "Agentic Marketing (yearly)" },
            { name: "Agentic Marketing (monthly)" },
            { name: "Done-For-You (6-month)" },
            { name: "Done-For-You (3-month)" },
          ],
        }),
        faqPage(pricingFaqs),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Pricing", url: "/pricing" },
        ]),
      ],
    }),
  component: PricingPage,
});

const agenticIncluded = [
  "Monthly AI Visibility Report across 4 LLMs",
  "Monthly Prompt Tracking and GEO Analytics",
  "Weekly Market Signals, Social Trends & Competitor Report",
  "Weekly trend-driven GEO blogs & Social Thought Leadership content",
  "10 GEO blogs, 10 LinkedIn posts, 30 tweets every week",
  "Real-time analytics",
  "Unlimited seats, priority support",
];

const dfyIncluded = [
  "Everything in Agentic Marketing",
  "Axy.digital strategist manages strategy, setup, and optimization",
  "Monthly check-in call",
  "Unlimited seats, priority support",
];

const pricingFaqs = [
  {
    q: "What happens during the free start?",
    a: "You get full access to the Axy.digital engine to scan your site and generate your first Market Map. You'll see exactly what Axy.digital plans to build before you ever pay a cent.",
  },
  {
    q: "What does the Axy.digital Strategist actually do?",
    a: "In the Done-For-You tier, your strategist acts as your fractional CMO. They don't just 'use the tool'—they interpret the market signals, adjust your brand voice, and ensure your campaigns are hitting your specific business KPIs.",
  },
  {
    q: "Can I switch between tiers?",
    a: "Absolutely. Many founders start with Done-For-You to get their engine perfectly tuned, then move to Agentic Marketing once they are comfortable with the automation.",
  },
];

function PricingPage() {
  return (
    <section className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-mono uppercase tracking-tighter text-muted-foreground mb-4">
            Pricing
          </p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            Pick how hands-on you want to be.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Run marketing autonomously with Axy.digital, or have a dedicated strategist run it for
            you. Same engine underneath.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:grid-rows-[auto_auto_auto_1fr_auto]">
          <PlanCard
            title="Agentic Marketing"
            description="Axy.digital's AI runs your marketing—market research, strategy, campaign generation, execution and performance measurement. You review, approve and direct in a few clicks."
            included={agenticIncluded}
          />
          <PlanCard
            title="Done For You"
            description="A dedicated Axy.digital strategist manages your strategy and content end-to-end, powered by the Axy.digital engine."
            included={dfyIncluded}
          />
        </div>

        {/* FAQ */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6 text-center">
            FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {pricingFaqs.map((item, i) => (
              <AccordionItem key={item.q} value={`item-${i}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function PlanCard({
  title,
  description,
  included,
}: {
  title: string;
  description: string;
  included: string[];
}) {
  return (
    <div className="flex flex-col md:grid md:row-span-5 md:grid-rows-subgrid rounded-2xl ring-1 ring-border bg-card p-8 md:p-10">
      <h2 className="text-2xl font-semibold tracking-tight mb-3">{title}</h2>
      <p className="text-muted-foreground mb-8">{description}</p>

      <p className="text-xs font-mono uppercase tracking-tighter text-muted-foreground mb-4">
        What's included
      </p>
      <ul className="space-y-3 mb-8">
        {included.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <Check className="size-4 mt-0.5 text-foreground shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <BookDemoDialog>
        <Button size="lg" className="w-full rounded-md">
          Talk to us
        </Button>
      </BookDemoDialog>
    </div>
  );
}
